import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizeVetCertificate, vetCertificatesApi } from '../../api/vetCertificatesApi';
import { useLocationOptions } from '../Admin/shared/useLocationOptions';
import { useBorderVetCreateOptions } from './useMarketTradReferences';
import NlimsDataTable from '../shared/NlimsDataTable/NlimsDataTable';
import { FilterBar, SearchRow } from './shared';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, actionCellHtml as sharedActionCellHtml, WORKFLOW_RULES } from '../shared/RowActionMenu';

const PURPOSE_OPTIONS = [
  { value: 'trade', label: 'Trade' },
  { value: 'movement', label: 'Movement' },
  { value: 'export', label: 'Export' },
  { value: 'slaughter', label: 'Slaughter' },
  { value: 'research', label: 'Research' },
  { value: 'import', label: 'Import' },
  { value: 'transit', label: 'Transit' },
];

function normalizeForCompare(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// Client-side filtering, mirroring the dropdown filter set — the backend
// doesn't reliably honor these query params, so filters are applied here
// against the already-loaded records instead (same approach Conflicts &
// Dispute Records uses).
function filterVetCertificateRows(rows, filters) {
  const state = normalizeForCompare(filters.state);
  const species = normalizeForCompare(filters.species_uuid);
  const purpose = normalizeForCompare(filters.purpose);

  return rows.filter((row) => {
    if (state && normalizeForCompare(row.state) !== state) return false;
    if (species) {
      const rowSpeciesUuid = normalizeForCompare(row.species_uuid ?? row.speciesUuid);
      const rowSpeciesCode = normalizeForCompare(row.species_code ?? row.speciesCode);
      if (rowSpeciesUuid !== species && rowSpeciesCode !== species) return false;
    }
    if (purpose && normalizeForCompare(row.purpose) !== purpose) return false;
    return true;
  });
}

function parseError(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function formatCount(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return '—';
  return number.toLocaleString('en-NG');
}

function buildLookup(options = []) {
  const map = new Map();
  options.forEach((option) => {
    const raw = option?.raw ?? {};
    [option?.value, raw.uuid, raw.id, raw.code, raw.name, raw.market_name, raw.marketName]
      .filter(Boolean)
      .forEach((key) => map.set(String(key), option.label ?? option.value ?? String(key)));
  });
  return map;
}

function normalizeVetRow(row = {}, lookups) {
  const normalized = normalizeVetCertificate(row);
  const originMarketKey = [normalized.origin_market_uuid, normalized.originMarketUuid].find(Boolean);
  const institutionKey  = [normalized.issuing_institution_uuid, normalized.issuingInstitutionUuid].find(Boolean);
  const speciesKey      = [normalized.species_uuid, normalized.speciesUuid, normalized.species_code, normalized.speciesCode].find(Boolean);

  return {
    ...normalized,
    origin_market_label:
      (originMarketKey && lookups.marketLookup.get(String(originMarketKey))) ||
      row.origin_market?.market_name || row.origin_market?.name || normalized.origin_market_uuid || '',
    issuing_institution_label:
      (institutionKey && lookups.institutionLookup.get(String(institutionKey))) ||
      row.issuing_institution?.name || normalized.issuing_institution_uuid || '',
    species_label:
      (speciesKey && lookups.speciesLookup.get(String(speciesKey))) ||
      normalized.species_label || normalized.speciesLabel || String(speciesKey ?? ''),
  };
}

function statusBadgeHtml(status) {
  if (!status) return '—';
  const key = String(status).trim().toLowerCase();
  // Workflow pipeline: exact spec colours (Grey/Blue/Amber/Green/Red)
  // Vet-specific states mapped to same RAG palette
  const styleMap = {
    draft:          'background:#f5f5f4;color:#888780;border:1px solid #d6d3d1;',
    submitted:      'background:#eff6ff;color:#185FA5;border:1px solid #bfdbfe;',
    validated:      'background:#fffbeb;color:#BA7517;border:1px solid #fde68a;',
    approved:       'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
    rejected:       'background:#fff1f2;color:#A32D2D;border:1px solid #fecdd3;',
    // Vet-specific statuses (DCIC-05: valid / expired / revoked)
    valid:          'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
    expired:        'background:#fffbeb;color:#BA7517;border:1px solid #fde68a;',
    revoked:        'background:#fff1f2;color:#A32D2D;border:1px solid #fecdd3;',
  };
  const style = styleMap[key] || 'background:#f5f5f4;color:#888780;border:1px solid #d6d3d1;';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;letter-spacing:.02em;white-space:nowrap;${style}">${status}</span>`;
}

// ── Shared edit-form primitives ───────────────────────────────────────────────
const INPUT_CLS =
  'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white ' +
  'focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 appearance-none';

function EField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function EInput({ value, onChange, placeholder = '', type = 'text', step, min, max }) {
  return (
    <input
      type={type}
      step={step}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      min={min}
      max={max}
      className={INPUT_CLS}
    />
  );
}

function ESelect({ value, onChange, options = [], placeholder = 'Select', disabled = false }) {
  return (
    <select
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      className={`${INPUT_CLS} disabled:bg-gray-50 disabled:text-gray-400`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : String(o.value ?? '');
        const l = typeof o === 'string' ? o : String(o.label ?? v);
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

// ── Role capability matrix (matches system RBAC spec) ────────────────────────
// R01: System Admin — full CRUD + validate + approve
// R02: National Data Manager — read/validate/approve only, no create/edit/delete
// R03: Federal Dept Officer — full CRUD + validate (primary entry for vet certs)
// R04: State Data Officer — read only (cannot create vet certs)
// R05: LGA Field Officer — no access to vet certificates
const ROLE_CAN_VET = {
  R01: { create: true,  edit: true,  submit: true,  validate: true,  approve: true,  reject: true,  delete: true  },
  R02: { create: false, edit: false, submit: false, validate: false, approve: true,  reject: true,  delete: false },
  R03: { create: true,  edit: true,  submit: true,  validate: true,  approve: false, reject: true,  delete: false },
  R04: { create: false, edit: false, submit: false, validate: false, approve: false, reject: false, delete: false },
  R05: { create: false, edit: false, submit: false, validate: false, approve: false, reject: false, delete: false },
};

// Per-role status-transition map, replicating getVetMenuItems()'s exact logic
// (R02, the national reviewer, may only reject an already-Validated
// certificate; R01/R03 can reject at Submitted or Validated). Passed as
// `transitions` to the shared ActionDropdown.
const VET_TRANSITIONS = {
  R01: { draft: ['submitted'], rejected: ['submitted'], submitted: ['validated', 'rejected'], validated: ['approved', 'rejected'] },
  R02: { validated: ['approved', 'rejected'] },
  R03: { draft: ['submitted'], rejected: ['submitted'], submitted: ['validated', 'rejected'], validated: ['rejected'] },
  R04: {},
  R05: {},
};

// R01 may edit at any status; R03 (the field/originator role) while
// Draft/Rejected/Submitted. Matches the original getVetMenuItems() showEdit rule.
const VET_EDITABLE_STATUSES = {
  R01: ['draft', 'submitted', 'validated', 'approved', 'rejected'],
  R03: ['draft', 'rejected', 'submitted'],
};

function InfoRow({ label, value, wide = false }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-800 break-words">{value || '—'}</p>
    </div>
  );
}

// ── Unified record modal (view → edit, with inline delete confirm) ─────────────
function VetRecordModal({ row, stateOptions, marketOptions, speciesOptions, institutionOptions, onSave, onDelete, onClose }) {
  const { user } = useAuth();
  // R03 (Federal Dept Officer) is the field/originator role for Vet Certificates —
  // R04 is read-only, R05 has no access, R01/R02 keep the existing single "Save Changes" behaviour.
  const isFieldUser = user?.roleId === 'R03';
  const currentStatus = (row.data_status ?? row.dataStatus ?? row.workflow_status ?? 'draft').toLowerCase();
  const [mode, setMode] = useState('view');
  const [showDel, setShowDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState('');

  // edit form state
  const [f, setF] = useState({
    certificate_number:       row.certificate_number       ?? '',
    issued_at:                row.issued_at                ?? '',
    expires_at:               row.expires_at               ?? '',
    issuing_officer_name:     row.issuing_officer_name     ?? '',
    issuing_authority_uuid:   row.issuing_authority_uuid   ?? '',
    origin_market_uuid:       row.origin_market_uuid       ?? '',
    state:                    row.state                    ?? '',
    species_uuid:             row.species_uuid             ?? '',
    species_uuids:            Array.isArray(row.species_uuids) && row.species_uuids.length > 0
                                 ? row.species_uuids
                                 : (row.species_uuid ? [row.species_uuid] : []),
    head_count:               String(row.head_count        ?? ''),
    destination:              row.destination              ?? '',
    purpose:                  row.purpose                  ?? '',
    status:                   row.status                   ?? '',
    border_crossing_uuid:     row.border_crossing_uuid     ?? '',
    reason_for_revocation:    row.reason_for_revocation    ?? '',
    notes:                    row.notes                    ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState('');

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const dateError =
    f.issued_at && f.expires_at && f.expires_at < f.issued_at
      ? 'Expires At must be on or after Issued At.'
      : '';

  const isRevoked = f.status === 'revoked';

  const canSave =
    !!f.certificate_number.trim() && !!f.issued_at && !!f.expires_at &&
    !!f.issuing_officer_name.trim() && !!f.issuing_authority_uuid &&
    !!f.origin_market_uuid && !!f.state && !!f.species_uuid &&
    Number(f.head_count) > 0 && !!f.destination.trim() && !!f.purpose &&
    !!f.status && (!isRevoked || !!f.reason_for_revocation.trim()) &&
    !dateError;

  const buildPayload = (extra = {}) => {
    // Preserve the original multi-species selection unless the user actually
    // changed the (single-select) species field — avoids silently truncating
    // a certificate that covers multiple species down to just one.
    const speciesUuids = f.species_uuid === (row.species_uuid ?? '') && f.species_uuids.length > 0
      ? f.species_uuids
      : (f.species_uuid ? [f.species_uuid] : []);
    return {
      certificate_number:       f.certificate_number.trim(),
      issued_at:                f.issued_at,
      expires_at:               f.expires_at,
      issuing_officer_name:     f.issuing_officer_name.trim(),
      issuing_authority_uuid:   f.issuing_authority_uuid,
      origin_market_uuid:       f.origin_market_uuid,
      state:                    f.state,
      species_uuid:             f.species_uuid,
      species_uuids:            speciesUuids,
      head_count:               Number(f.head_count),
      destination:              f.destination.trim(),
      purpose:                  f.purpose,
      status:                   f.status,
      border_crossing_uuid:     f.border_crossing_uuid || null,
      // Field/originator role (R03): saving always defaults the record back to draft —
      // advancing the workflow is a separate, explicit "Submit for Review" action.
      // Other roles (R01/R02): preserve whatever status the record currently has.
      data_status:              isFieldUser ? 'draft' : (row.data_status ?? row.dataStatus ?? row.workflow_status ?? 'draft'),
      reason_for_revocation:    isRevoked ? f.reason_for_revocation.trim() : null,
      notes:                    f.notes.trim() || null,
      raw:                      row.raw,
      ...extra,
    };
  };

  const handleSave = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    setSaveError('');
    try {
      await onSave(row.uuid, buildPayload());
      onClose();
    } catch (err) {
      setSaveError(parseError(err, 'Failed to save changes.'));
      setBusy(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    setSaveError('');
    try {
      await onSave(row.uuid, buildPayload({ submitAfterSave: true }));
      onClose();
    } catch (err) {
      setSaveError(parseError(err, 'Failed to save changes.'));
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setDelBusy(true);
    setDelError('');
    try { await onDelete(row.uuid); }
    catch (err) { setDelError(parseError(err, 'Failed to delete.')); setDelBusy(false); }
  };

  const CloseBtn = () => (
    <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );

  if (showDel) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl p-6">
          <h3 className="text-base font-bold text-gray-900 mb-2">Delete Record</h3>
          <p className="text-sm text-gray-600 mb-1">
            Are you sure you want to delete <strong className="text-gray-900">Cert # {row.certificate_number}</strong>? This action cannot be undone.
          </p>
          {delError && <p className="text-xs text-red-600 mt-2">{delError}</p>}
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setShowDel(false); setDelError(''); }} className="flex-1 h-10 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={handleDelete} disabled={delBusy} className="flex-1 h-10 rounded-full bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {delBusy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'edit') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between border-b border-green-100 bg-green-50 px-6 py-4 flex-shrink-0">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-green-600">Edit Vet Certificate</p>
              <h3 className="mt-0.5 text-sm font-semibold text-green-900">Cert # {row.certificate_number}</h3>
            </div>
            <CloseBtn />
          </div>
          <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1">
            {saveError && <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-sm text-red-700">{saveError}</div>}
            <div className="grid grid-cols-2 gap-3">
              <EField label="Certificate Number" required>
                <EInput value={f.certificate_number} onChange={set('certificate_number')} placeholder="e.g. VET-202408-0001" />
              </EField>
              <EField label="Status" required>
                <ESelect value={f.status} onChange={set('status')} options={[
                  { value: 'valid',   label: 'Valid' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'revoked', label: 'Revoked' },
                ]} placeholder="Select status" />
              </EField>
              <EField label="Issued At" required>
                <EInput type="date" value={f.issued_at} onChange={set('issued_at')} />
              </EField>
              <EField label="Expires At" required>
                <EInput type="date" value={f.expires_at} onChange={set('expires_at')} min={f.issued_at || undefined} />
                {dateError && <p className="mt-1 text-[11px] text-red-600">{dateError}</p>}
              </EField>
              <EField label="Issuing Officer" required>
                <EInput value={f.issuing_officer_name} onChange={set('issuing_officer_name')} placeholder="e.g. Dr. A. Bello" />
              </EField>
              <EField label="Issuing Authority" required>
                <ESelect value={f.issuing_authority_uuid} onChange={set('issuing_authority_uuid')} options={institutionOptions} placeholder="Select authority" />
              </EField>
              <EField label="Origin Market" required>
                <ESelect value={f.origin_market_uuid} onChange={set('origin_market_uuid')} options={marketOptions} placeholder="Select market" />
              </EField>
              <EField label="State" required>
                <ESelect value={f.state} onChange={set('state')} options={stateOptions} placeholder="Select state" />
              </EField>
              <EField label="Species" required>
                <ESelect value={f.species_uuid} onChange={set('species_uuid')} options={speciesOptions} placeholder="Select species" />
              </EField>
              <EField label="Head Count" required>
                <EInput type="number" step="1" value={f.head_count} onChange={set('head_count')} placeholder="e.g. 100" />
              </EField>
              <EField label="Purpose" required>
                <ESelect value={f.purpose} onChange={set('purpose')} options={PURPOSE_OPTIONS} placeholder="Select purpose" />
              </EField>
            </div>
            <EField label="Destination" required>
              <EInput value={f.destination} onChange={set('destination')} placeholder="e.g. Kano central abattoir" />
            </EField>
            {f.border_crossing_uuid && (
              <InfoRow label="Linked Border Crossing Reference" value={f.border_crossing_uuid} />
            )}
            {isRevoked && (
              <EField label="Reason for Revocation" required>
                <textarea value={f.reason_for_revocation} onChange={set('reason_for_revocation')} rows={2}
                  placeholder="State the reason this certificate is being revoked"
                  className={`${INPUT_CLS} resize-none border-red-200 bg-red-50 focus:border-red-400`} />
              </EField>
            )}
            <EField label="Notes">
              <textarea value={f.notes} onChange={set('notes')} rows={2} placeholder="Optional notes" className={`${INPUT_CLS} resize-none`} />
            </EField>
          </div>
          <div className="flex gap-3 px-6 pb-6 flex-shrink-0">
            <button onClick={() => setMode('view')} className="flex-1 h-11 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
            {isFieldUser ? (
              <>
                <button onClick={handleSave} disabled={!canSave || busy} className="flex-1 h-11 rounded-full border border-green-300 bg-white text-sm font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed">
                  {busy ? 'Saving…' : (currentStatus === 'rejected' ? 'Save Changes' : 'Save as Draft')}
                </button>
                <button onClick={handleSaveAndSubmit} disabled={!canSave || busy} className="flex-1 h-11 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  {busy ? 'Submitting…' : 'Submit for Review'}
                </button>
              </>
            ) : (
              <button onClick={handleSave} disabled={!canSave || busy} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-green-100 bg-green-50 px-6 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-green-600">Record Details</p>
            <h3 className="mt-0.5 text-sm font-semibold text-green-900">Cert # {row.certificate_number || '—'}</h3>
          </div>
          <CloseBtn />
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 px-6 py-5">
          <InfoRow label="Certificate Number" value={row.certificate_number} />
          <InfoRow label="Status" value={row.status} />
          <InfoRow label="State" value={row.state} />
          <InfoRow label="Issued At" value={row.issued_at} />
          <InfoRow label="Expires At" value={row.expires_at} />
          <InfoRow label="Issuing Institution" value={row.issuing_institution_label} />
          <InfoRow label="Issuing Authority" value={row.issuing_authority_label || row.issuing_officer_name || '—'} />
          <InfoRow label="Origin Market" value={row.origin_market_label} />
          <InfoRow label="Species" value={row.species_label} />
          <InfoRow label="Head Count" value={formatCount(row.head_count)} />
          <InfoRow label="Destination" value={row.destination} />
          <InfoRow label="Purpose" value={row.purpose} />
          {row.border_crossing_uuid && (
            <InfoRow label="Border Crossing Ref." value={row.border_crossing_uuid} />
          )}
          {row.reason_for_revocation && (
            <InfoRow label="Reason for Revocation" value={row.reason_for_revocation} wide />
          )}
          <InfoRow label="Notes" value={row.notes} wide />
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">Close</button>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowDel(true)} className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
            <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Table columns ─────────────────────────────────────────────────────────────
const TABLE_COLUMNS = [
  { title: 'Certificate #', data: 'certificate_number', sortable: true },
  { title: 'State', data: 'state', sortable: true },
  { title: 'Origin Market', data: 'origin_market_label', sortable: true },
  { title: 'Issuing Institution', data: 'issuing_institution_label', sortable: true },
  { title: 'Issued At', data: 'issued_at', sortable: true },
  { title: 'Expires At', data: 'expires_at', sortable: true },
  { title: 'Species', data: 'species_label', sortable: true },
  {
    title: 'Head Count',
    data: 'head_count',
    sortable: true,
    render: (data, type) => (type === 'display' ? formatCount(data) : (data ?? 0)),
  },
  { title: 'Issuing Officer', data: 'issuing_officer_name', sortable: true },
  { title: 'Destination', data: 'destination', sortable: true },
  { title: 'Purpose', data: 'purpose', sortable: true },
  { title: 'Certificate Status', data: 'status', sortable: true, defaultContent: '',
    render: (v, type) => (type === 'display' && v ? String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : (v ?? '')) },
  { title: 'Linked Border Crossing', data: 'border_crossing_uuid', sortable: true, defaultContent: '',
    render: (v, type) => (type === 'display' ? (v ? 'Linked' : '—') : (v ? 'Linked' : '')) },
  {
    title: 'Workflow Status',
    data: 'data_status',
    sortable: true,
    render: (data, type) => (type === 'display' ? statusBadgeHtml(data || 'draft') : (data ?? 'draft')),
  },
  {
    title: 'Action',
    data: null,
    sortable: false,
    render: (_d, type) => (type === 'display' ? sharedActionCellHtml() : ''),
  },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function VetCertificatesRegistry({ onCreateNew, refreshKey = 0, newRecord }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? 'R06';
  const { pushWorkflowNotification } = useNotifications();
  const [draftFilters, setDraftFilters] = useState({ state: '', species_uuid: '', purpose: '' });
  const [appliedFilters, setAppliedFilters] = useState({ state: '', species_uuid: '', purpose: '' });
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState('');

  const { stateOptions, loadingStates } = useLocationOptions();
  const {
    marketOptions,
    speciesOptions,
    institutionOptions,
    loading: referenceLoading,
    error: referenceError,
  } = useBorderVetCreateOptions();

  const lookups = useMemo(() => ({
    marketLookup:      buildLookup(marketOptions),
    speciesLookup:     buildLookup(speciesOptions),
    institutionLookup: buildLookup(institutionOptions),
  }), [institutionOptions, marketOptions, speciesOptions]);
  // Ref so fetch effect always uses latest lookups without triggering extra re-fetches
  const lookupsRef = useRef(lookups);
  useEffect(() => { lookupsRef.current = lookups; }, [lookups]);
  useEffect(() => {
    setRecords((prev) => prev.map((row) => normalizeVetRow(row, lookupsRef.current)));
  }, [lookups]);

  // Insert freshly created record into local state (avoids re-fetch which loses status)
  useEffect(() => {
    if (!newRecord?.uuid) return;
    const normalized = normalizeVetRow(newRecord, lookups);
    setRecords((prev) => {
      if (prev.some((r) => r.uuid === normalized.uuid)) return prev;
      return [normalized, ...prev];
    });
  }, [newRecord, lookups]);

  const filterDefs = useMemo(() => ([
    {
      key: 'state',
      label: 'State',
      options: stateOptions,
      defaultLabel: loadingStates ? 'Loading States...' : 'All States',
    },
    {
      key: 'species_uuid',
      label: 'Species',
      options: speciesOptions,
      defaultLabel: referenceLoading ? 'Loading species...' : 'All Species',
    },
    {
      key: 'purpose',
      label: 'Purpose',
      options: PURPOSE_OPTIONS,
      defaultLabel: 'All Purposes',
    },
  ]), [loadingStates, referenceLoading, speciesOptions, stateOptions]);

  // Fetch once per refreshKey — dropdown filters are applied client-side below
  // (mirrors Conflicts & Dispute Records, since the backend doesn't reliably
  // honor these query params).
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    vetCertificatesApi.listAll({ per_page: 1000 })
      .then((payload) => {
        if (!active) return;
        setRecords((payload.items || []).map((row) => normalizeVetRow(row, lookupsRef.current)));
      })
      .catch((err) => {
        if (active) { setError(parseError(err, 'Failed to load veterinary certificate records.')); setRecords([]); }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Applies immediately on every change — live filtering, matching the rest
  // of the app, instead of requiring a separate "Apply" click.
  const handleFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => {
      const next = { ...prev, [key]: value };
      setAppliedFilters(next);
      return next;
    });
  }, []);

  const handleApply = useCallback(() => setAppliedFilters(draftFilters), [draftFilters]);

  const dropdownFilteredRecords = useMemo(
    () => filterVetCertificateRows(records, appliedFilters),
    [records, appliedFilters]
  );

  // Search is client-side on top of the dropdown-filtered records
  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dropdownFilteredRecords;
    return dropdownFilteredRecords.filter((row) =>
      [
        row.certificate_number, row.state, row.origin_market_label,
        row.issuing_institution_label, row.species_label, row.destination,
        row.issuing_officer_name, row.purpose, row.status,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [dropdownFilteredRecords, search]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action === 'view') setActionMenu({ row, rect });
  }, []);

  const reloadRecords = useCallback(() => {
    setLoading(true);
    setError('');
    vetCertificatesApi.listAll({ per_page: 1000 })
      .then((payload) => setRecords((payload.items || []).map((row) => normalizeVetRow(row, lookupsRef.current))))
      .catch((err) => setError(parseError(err, 'Failed to load vet certificate records.')))
      .finally(() => setLoading(false));
  }, []);

  const handleQuickSubmit = useCallback(async (row) => {
    setQuickBusy('Submitting…');
    try {
      await vetCertificatesApi.submit(row.uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN_VET, newStatus: 'submitted', actorRoleId: roleId, recordLabel: row.certificate_number });
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Submit failed.')); }
    finally { setQuickBusy(''); }
  }, [reloadRecords, roleId, pushWorkflowNotification]);

  const handleQuickValidate = useCallback(async (row) => {
    setQuickBusy('Validating…');
    try {
      await vetCertificatesApi.validate(row.uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN_VET, newStatus: 'validated', actorRoleId: roleId, recordLabel: row.certificate_number });
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Validation failed.')); }
    finally { setQuickBusy(''); }
  }, [reloadRecords, roleId, pushWorkflowNotification]);

  const handleQuickApprove = useCallback(async (row) => {
    setQuickBusy('Approving…');
    try {
      await vetCertificatesApi.approve(row.uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN_VET, newStatus: 'approved', actorRoleId: roleId, recordLabel: row.certificate_number });
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Approval failed.')); }
    finally { setQuickBusy(''); }
  }, [reloadRecords, roleId, pushWorkflowNotification]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy('Rejecting…');
    try {
      await vetCertificatesApi.reject(rejectTarget.uuid, notes);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN_VET, newStatus: 'rejected', actorRoleId: roleId, recordLabel: rejectTarget.certificate_number, notes });
      setRejectTarget(null);
      reloadRecords();
    }
    catch (e) { setError(parseError(e, 'Reject failed.')); throw e; }
    finally { setQuickBusy(''); }
  }, [rejectTarget, reloadRecords, roleId, pushWorkflowNotification]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy('Deleting…');
    try { await vetCertificatesApi.remove(deleteTarget.uuid); setDeleteTarget(null); reloadRecords(); }
    catch (e) { setError(parseError(e, 'Delete failed.')); }
    finally { setQuickBusy(''); }
  }, [deleteTarget, reloadRecords]);

  const handleSave = useCallback(async (uuid, payload) => {
    const { submitAfterSave, ...body } = payload;
    // Confirm the update succeeded (throws on error)
    await vetCertificatesApi.update(uuid, body);
    if (submitAfterSave) {
      await vetCertificatesApi.submit(uuid);
      pushWorkflowNotification({ moduleKey: 'markets', roleCan: ROLE_CAN_VET, newStatus: 'submitted', actorRoleId: roleId, recordLabel: body.certificate_number });
    }
    // Use payload values (what the user submitted) for the local optimistic patch;
    // don't trust the server response shape, which may omit status and other fields.
    // Preserve the original status when the user didn't explicitly set one.
    setRecords((prev) =>
      prev.map((r) => {
        if (r.uuid !== uuid) return r;
        const status = body.status || r.status || '';
        const dataStatus = submitAfterSave
          ? 'submitted'
          : (body.data_status || r.data_status || r.dataStatus || r.workflow_status || 'draft');
        return normalizeVetRow({ ...r, ...body, status, data_status: dataStatus, dataStatus }, lookups);
      })
    );
  }, [lookups, roleId, pushWorkflowNotification]);

  const handleDelete = useCallback(async (uuid) => {
    await vetCertificatesApi.remove(uuid);
    setRecords((prev) => prev.filter((r) => r.uuid !== uuid));
    setViewRow(null);
  }, []);

  return (
    <div className="space-y-4">
      <FilterBar
        filters={filterDefs}
        filterValues={draftFilters}
        onFilterChange={handleFilterChange}
        onApply={handleApply}
      />

      <SearchRow
        searchValue={search}
        onSearch={setSearch}
        onCreate={onCreateNew ?? (() => {})}
        placeholder="Search by certificate number, market, or destination..."
        canCreate={ROLE_CAN_VET[roleId]?.create === true}
      />

      {referenceError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{referenceError}</div>
      )}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {loading && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading veterinary certificate records...
        </div>
      )}

      <NlimsDataTable
        tableId="vet-certificates-table"
        columns={TABLE_COLUMNS}
        data={filteredRecords}
        serverSide={false}
        hideSearch
        pageLength={25}
        onRowAction={handleRowAction}
        defaultSort={[4, 'desc']}
      />

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.vetCertificates}
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/markets-trade/view/${actionMenu.row.uuid}?subModule=vet`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/markets-trade/edit/${actionMenu.row.uuid}?subModule=vet`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => { const r = actionMenu.row; setActionMenu(null); handleQuickSubmit(r); }}
          onValidate={() => { const r = actionMenu.row; setActionMenu(null); handleQuickValidate(r); }}
          onApprove={() => { const r = actionMenu.row; setActionMenu(null); handleQuickApprove(r); }}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {viewRow && (
        <VetRecordModal
          row={viewRow}
          stateOptions={stateOptions}
          marketOptions={marketOptions}
          speciesOptions={speciesOptions}
          institutionOptions={institutionOptions}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setViewRow(null)}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={deleteTarget.certificate_number ? `Cert # ${deleteTarget.certificate_number}` : ''}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={!!quickBusy}
        />
      )}
    </div>
  );
}
